// AUTO-GENERATED FILE - DO NOT EDIT
// Generated by scripts/generate-cube-meta.ts
import type {
  CubeMeasureEntry,
  CubeDimensionEntry,
  CubeJoinEntry
} from 'shared/src/api/cube-api-types';

export const campaignDailyMetricsCubeMeta = {
  name: 'campaign_daily_metrics',
  sqlTable: 'metrics.campaign_daily_metrics',
  title: 'Campaign Daily Metrics',
  description: 'Daily grain metrics per campaign. Use for time-series.',
  joins: {
    campaigns: { relationship: 'many_to_one' } as const satisfies CubeJoinEntry
  },
  measures: {
    'campaign_daily_metrics.uncapped_revenue': {
      sql: 'uncapped_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Uncapped Revenue (Raw)',
      format: 'currency',
      description:
        'Sum of raw uncapped_revenue (before commission adjustment and before any cap). Use campaigns cube for per-campaign metrics.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(uncapped_revenue)',
        inputs: [],
        short_description:
          'Daily uncapped revenue (gross, before referral commission and caps) for a campaign',
        source_tables: ['metrics.campaign_daily_metrics'],
        rolled_up_from: 'line_item_metrics_daily.uncapped_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaign_daily_metrics.actual_spend': {
      sql: 'actual_spend',
      type: 'number',
      aggType: 'sum',
      title: 'Actual Spend',
      format: 'currency',
      description: 'Daily actual media spend across all line items for a campaign',
      meta: {
        category: 'spend',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(actual_spend)',
        inputs: [],
        short_description: 'Daily actual media spend for a campaign',
        source_tables: ['metrics.campaign_daily_metrics'],
        rolled_up_from: 'line_item_metrics_daily.actual_spend'
      }
    } as const satisfies CubeMeasureEntry,
    'campaign_daily_metrics.planned_spend': {
      sql: 'planned_spend',
      type: 'number',
      aggType: 'sum',
      title: 'Planned Spend',
      format: 'currency',
      description: 'Daily planned media spend across all line items for a campaign',
      meta: {
        category: 'spend',
        grain: 'daily',
        period: 'daily',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(planned_spend)',
        inputs: [],
        short_description: 'Daily planned media spend for a campaign',
        source_tables: ['metrics.campaign_daily_metrics'],
        rolled_up_from: 'line_item_metrics_daily.planned_spend'
      }
    } as const satisfies CubeMeasureEntry,
    'campaign_daily_metrics.media_cost': {
      sql: 'media_cost',
      type: 'number',
      aggType: 'sum',
      title: 'Media Cost',
      format: 'currency',
      description:
        'Daily media cost (COGS). Excludes management fee line items (they contribute 0).',
      meta: {
        category: 'spend',
        grain: 'daily',
        period: 'daily',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(media_cost)',
        inputs: [],
        short_description:
          'Daily media cost (COGS). Excludes management_fee line items (0 contribution)',
        source_tables: ['metrics.campaign_daily_metrics'],
        rolled_up_from: 'line_item_metrics_daily.media_cost'
      }
    } as const satisfies CubeMeasureEntry,
    'campaign_daily_metrics.planned_revenue': {
      sql: 'planned_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Planned Revenue',
      format: 'currency',
      description: 'Daily planned revenue across all line items for a campaign',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'daily',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(planned_revenue)',
        inputs: [],
        short_description: 'Daily planned revenue for a campaign',
        source_tables: ['metrics.campaign_daily_metrics'],
        rolled_up_from: 'line_item_metrics_daily.planned_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaign_daily_metrics.cumulative_planned_revenue': {
      sql: 'cumulative_planned_revenue',
      type: 'number',
      aggType: 'max',
      title: 'Cumulative Planned Revenue',
      format: 'currency',
      description:
        'Running total of planned revenue through this day. Use MAX per campaign (pre-computed window function).',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'cumulative',
        type: 'planned',
        supports_rollup: false,
        formula: 'MAX(cumulative_planned_revenue)',
        inputs: [],
        short_description: 'Running total of planned revenue through this day',
        source_tables: ['metrics.campaign_daily_metrics'],
        rolled_up_from: 'line_item_metrics_daily.planned_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaign_daily_metrics.monthly_capped_actual_revenue': {
      sql: 'monthly_capped_actual_revenue',
      type: 'number',
      aggType: 'max',
      title: 'Monthly Capped Revenue',
      format: 'currency',
      description:
        'Actual revenue capped monthly per line item, then summed to campaign. Bottom-up LEAST per LI.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'monthly',
        type: 'capped_actual',
        supports_rollup: false,
        supports_timeseries: true,
        required_dimensions: ['campaign_daily_metrics.campaign_id', 'campaign_daily_metrics.day'],
        formula: 'MAX(monthly_capped_actual_revenue)',
        inputs: [],
        short_description:
          'Actual revenue capped monthly per line item, then summed to campaign level',
        source_tables: ['metrics.campaign_daily_metrics'],
        rolled_up_from: 'line_item_metrics_daily.monthly_capped_actual_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaign_daily_metrics.lifetime_capped_actual_revenue': {
      sql: 'lifetime_capped_actual_revenue',
      type: 'number',
      aggType: 'max',
      title: 'Lifetime Capped Revenue',
      format: 'currency',
      description:
        'Actual revenue capped to LI price, then summed to campaign. Bottom-up LEAST per LI.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'lifetime',
        type: 'capped_actual',
        supports_rollup: false,
        supports_timeseries: true,
        required_dimensions: ['campaign_daily_metrics.campaign_id', 'campaign_daily_metrics.day'],
        formula: 'MAX(lifetime_capped_actual_revenue)',
        inputs: [],
        short_description:
          'Actual revenue capped at line item price (lifetime), then summed to campaign level',
        source_tables: ['metrics.campaign_daily_metrics'],
        rolled_up_from: 'line_item_metrics_daily.lifetime_capped_actual_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaign_daily_metrics.effective_monthly_capped_actual_revenue': {
      sql: 'campaign_daily_metrics.monthly_capped_actual_revenue * (1 - COALESCE(campaign_daily_metrics.campaigns.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'max',
      title: 'Effective Monthly Capped Revenue',
      format: 'currency',
      description: 'Monthly capped revenue after deducting referral commission.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'monthly',
        type: 'effective_capped_actual',
        supports_rollup: false,
        supports_timeseries: true,
        required_dimensions: ['campaign_daily_metrics.campaign_id', 'campaign_daily_metrics.day'],
        formula: 'monthly_capped_actual_revenue * (1 - referral_commission_rate)',
        inputs: [
          'campaign_daily_metrics.monthly_capped_actual_revenue',
          'campaigns.referral_commission_rate'
        ],
        short_description: 'Monthly capped revenue (after referral commission)',
        source_tables: ['metrics.campaign_daily_metrics'],
        rolled_up_from: 'line_item_metrics_daily.effective_monthly_capped_actual_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaign_daily_metrics.effective_lifetime_capped_actual_revenue': {
      sql: 'campaign_daily_metrics.lifetime_capped_actual_revenue * (1 - COALESCE(campaign_daily_metrics.campaigns.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'max',
      title: 'Effective Lifetime Capped Revenue',
      format: 'currency',
      description: 'Lifetime capped revenue after deducting referral commission.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'lifetime',
        type: 'effective_capped_actual',
        supports_rollup: false,
        supports_timeseries: true,
        required_dimensions: ['campaign_daily_metrics.campaign_id', 'campaign_daily_metrics.day'],
        formula: 'lifetime_capped_actual_revenue * (1 - referral_commission_rate)',
        inputs: [
          'campaign_daily_metrics.lifetime_capped_actual_revenue',
          'campaigns.referral_commission_rate'
        ],
        short_description: 'Lifetime capped revenue (after referral commission)',
        source_tables: ['metrics.campaign_daily_metrics'],
        rolled_up_from: 'line_item_metrics_daily.effective_lifetime_capped_actual_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'campaign_daily_metrics.current_monthly_capped_actual_revenue': {
      sql: "MAX(CASE WHEN date_trunc('month', campaign_daily_metrics.day) = date_trunc('month', CURRENT_DATE) THEN campaign_daily_metrics.monthly_capped_actual_revenue END)",
      type: 'number',
      aggType: 'number',
      title: 'Current Monthly Capped Revenue',
      format: 'currency',
      description: 'Monthly capped revenue for the current month only.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'current',
        type: 'capped_actual',
        supports_rollup: false,
        supports_timeseries: false,
        required_dimensions: ['campaign_daily_metrics.campaign_id'],
        formula: 'MAX(monthly_capped_actual_revenue) WHERE month = current month',
        inputs: ['campaign_daily_metrics.monthly_capped_actual_revenue'],
        short_description: 'Monthly capped revenue for current month only',
        source_tables: ['metrics.campaign_daily_metrics']
      }
    } as const satisfies CubeMeasureEntry,
    'campaign_daily_metrics.current_effective_monthly_capped_actual_revenue': {
      sql: "MAX(CASE WHEN date_trunc('month', campaign_daily_metrics.day) = date_trunc('month', CURRENT_DATE) THEN campaign_daily_metrics.monthly_capped_actual_revenue * (1 - COALESCE(campaign_daily_metrics.campaigns.referral_commission_rate, 0)) END)",
      type: 'number',
      aggType: 'number',
      title: 'Current Effective Monthly Capped Revenue',
      format: 'currency',
      description: 'Effective monthly capped revenue for the current month only.',
      meta: {
        category: 'revenue',
        grain: 'daily',
        period: 'current',
        type: 'effective_capped_actual',
        supports_rollup: false,
        supports_timeseries: false,
        required_dimensions: ['campaign_daily_metrics.campaign_id'],
        formula:
          'MAX(monthly_capped_actual_revenue * (1 - referral_commission_rate)) WHERE month = current month',
        inputs: [
          'campaign_daily_metrics.monthly_capped_actual_revenue',
          'campaigns.referral_commission_rate'
        ],
        short_description:
          'Effective monthly capped revenue (after referral commission) for current month only',
        source_tables: ['metrics.campaign_daily_metrics']
      }
    } as const satisfies CubeMeasureEntry
  },
  dimensions: {
    'campaign_daily_metrics.id': {
      sql: "campaign_daily_metrics.campaign_id || '-' || campaign_daily_metrics.day::text",
      type: 'string',
      title: 'Id',
      primaryKey: true,
      meta: {
        short_description:
          'Composite key (campaign_id + day) uniquely identifying each daily metric row',
        source_tables: ['metrics.campaign_daily_metrics']
      }
    } as const satisfies CubeDimensionEntry,
    'campaign_daily_metrics.campaign_id': {
      sql: 'campaign_id',
      type: 'string',
      title: 'Campaign Id',
      meta: {
        short_description: 'Parent campaign identifier. Filter/group by campaign',
        source_tables: ['metrics.campaign_daily_metrics']
      }
    } as const satisfies CubeDimensionEntry,
    'campaign_daily_metrics.day': {
      sql: 'day',
      type: 'time',
      title: 'Day',
      meta: {
        short_description: 'Calendar date for this daily metric record',
        source_tables: ['metrics.campaign_daily_metrics']
      }
    } as const satisfies CubeDimensionEntry
  }
};
